import React, {useState, useEffect, useContext} from 'react';
import {Switch, Route, useRouteMatch} from "react-router-dom";
import Search from './Search/Search'
import SearchV2 from '../TargetingV2/TargetingV2'
import Admin from '../TargetingV2/Admin/Admin'
import Spinner from '../../components/Spinner'
import Axios from '../../components/Axios'
import { withAITracking } from '@microsoft/applicationinsights-react-js';
import { reactPlugin } from '../../AppInsights';
import useTargetingPermissions from '../TargetingV2/hooks/useTargetingPermissions'
import useTargetingExperienceV2 from '../../hooks/useTargetingExperienceV2'
import { ExercisesContext } from '../../ExercisesContext'

function Targeting(props) {
    let {path, url} = useRouteMatch()

    const exercises = useContext(ExercisesContext)
    const targetingExercises = exercises?.targeting

    const { isAdminUser, isMultiExerciseUser, isElevatedViewer } = useTargetingPermissions()
    // For Arm 5 (single-exercise rep): check exercise.TEAM against the V2 team list.
    // Arm 1 (admin) resolves the exercise per-route in AdminSearchRoute instead.
    const singleExercise = targetingExercises?.[0]
    const isV2 = useTargetingExperienceV2(singleExercise)

    const [loaded, setLoaded] = useState(false)
    const [approved, setApproved] = useState(false)
    const [backUrl, setBackUrl] = useState("/")

    useEffect(() => {
        if (targetingExercises) {
            setLoaded(true)
        }
    }, [targetingExercises])

    // Approval check only runs for single-exercise arms (arms 4 & 5).
    // Multi-exercise arms delegate this to Search/Admin after resolving their exercise.
    useEffect(() => {
        if (loaded && !isAdminUser && !isMultiExerciseUser) {
            const exercise = targetingExercises[0]
            Axios('/api/v1/targeting/approval-check', { ID: exercise.ID }, (data) => {
                setApproved(data.length > 0)
            })
        }
    }, [loaded])

    if (!loaded) {
        return <Spinner />
    }

    // Arm 1 — isAdminUser (ADMIN + DBD/IDD on 31/32): full admin capabilities
    if (isAdminUser) {
        return (
            <Switch>
                <Route path={`${path}/search/:idSlug/:terrSlug`} render={({ match }) => {
                    const exercise = targetingExercises.find(ex => ex.ID == match.params.idSlug)
                    return <AdminSearchRoute key={exercise?.ID} url={backUrl} backUrl={backUrl} setBackUrl={setBackUrl} approved={approved} setApproved={setApproved} {...props} exercise={exercise} />
                }} />
                <Route path={`${path}/admin/:idSlug`} render={({ match }) => {
                    const exercise = targetingExercises.find(ex => ex.ID == match.params.idSlug)
                    return <Admin key={exercise?.ID} path={`${path}/admin/:idSlug`} approved={approved} setApproved={setApproved} {...props} exercise={exercise} />
                }} />
            </Switch>
        )
    }

    // Arm 2 — multi-exercise + elevated viewer: admin panel read-only.
    // Covers OAS/OBD on 21/31/32 AND cross-team elevated viewers (e.g. OBD on 13 who receives
    // both TM13 and TM15 exercises via crossTeamExerciseAccess config).
    if ((isMultiExerciseUser || targetingExercises.length > 1) && isElevatedViewer) {
        return (
            <Switch>
                <Route path={`${path}/search/:idSlug/:terrSlug`} render={({ match }) => {
                    const exercise = targetingExercises.find(ex => ex.ID == match.params.idSlug)
                    return <AdminSearchRoute key={exercise?.ID} url={url} approved={approved} setApproved={setApproved} {...props} exercise={exercise} />
                }} />
                <Route path={`${path}/:idSlug`} render={({ match }) => {
                    const exercise = targetingExercises.find(ex => ex.ID == match.params.idSlug)
                    return <Admin key={exercise?.ID} path={`${path}/:idSlug`} approved={approved} setApproved={setApproved} {...props} exercise={exercise} />
                }} />
            </Switch>
        )
    }

    // Arm 3 — isMultiExerciseUser (regular reps on 21/31/32): search only
    if (isMultiExerciseUser) {
        return (
            <Switch>
                <Route path={`${path}/:idSlug`} render={({ match }) => {
                    const exercise = targetingExercises.find(ex => ex.ID == match.params.idSlug)
                    return <MultiExerciseRoute key={exercise?.ID} url={url} approved={approved} setApproved={setApproved} {...props} exercise={exercise} />
                }} />
            </Switch>
        )
    }

    // Arms 4 & 5 — single exercise. Resolve once here; children receive a plain object.
    const exercise = singleExercise

    // Arm 4 — isElevatedViewer (OAS/OBD on 13/14/15): admin panel read-only
    if (isElevatedViewer) {
        const SearchComponent = isV2 ? SearchV2 : Search
        return (
            <Switch>
                {/* Two-segment URL (/search/:idSlug/:terrSlug) generated by Admin "View" links */}
                <Route path={`${path}/search/:idSlug/:terrSlug`}>
                    <SearchComponent url={url} approved={approved} {...props} exercise={exercise} />
                </Route>
                <Route path={`${path}/search/:terrSlug`}>
                    <SearchComponent url={url} approved={approved} {...props} exercise={exercise} />
                </Route>
                {/* Catch-all: handles /targeting, /targeting/, and /targeting/:id (e.g. from navbar) */}
                <Route path={path}>
                    <Admin path={`${path}`} approved={approved} setApproved={setApproved} {...props} exercise={exercise} />
                </Route>
            </Switch>
        )
    }

    // Arm 5 — standard single-exercise user (13/14/15)
    return isV2
        ? <SearchV2 url={url} approved={approved} {...props} exercise={exercise} />
        : <Search url={url} approved={approved} {...props} exercise={exercise} />
}

export default withAITracking(reactPlugin, Targeting);

// Inner component so the hook can be called after the exercise is resolved per-route
function AdminSearchRoute({ exercise, ...rest }) {
    const isV2 = useTargetingExperienceV2(exercise)
    const SearchComponent = isV2 ? SearchV2 : Search
    if (!exercise) return <Spinner />
    return <SearchComponent exercise={exercise} {...rest} />
}

// Inner component for Arm 3 — resolves V2 eligibility after the exercise is known per-route
function MultiExerciseRoute({ exercise, ...rest }) {
    const isV2 = useTargetingExperienceV2(exercise)
    const SearchComponent = isV2 ? SearchV2 : Search
    if (!exercise) return <Spinner />
    return <SearchComponent exercise={exercise} {...rest} />
}